import CloseIcon from '@mui/icons-material/AddRounded'
import { FormEvent, useEffect, useRef, useState, type JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from '@base-ui/react/dialog'
import ArrowLeftIcon from '@assets/images/arrow-left-icon.svg?react'
import { Button } from '@components/Button'
import { SubmitHoldProvider, useSubmitHolds } from '@components/SubmitHold'
import {
  BottomContainer,
  CancelButton,
  CloseButton,
  DialogForm,
  HeaderContainer,
  HeaderTitleContainer,
  MainContainer,
  PopUpContainer,
  PreviousButton,
  ScrollContainer,
  Title,
  TitleContainer,
} from './PopUp.styled'
import { focusFirstField } from './focus'

export interface PopUpSkeletonProps {
  children?: React.ReactNode
  fullScreen?: boolean
  // Edge-to-edge popups (lists, pickers) opt out of the shared 24px body gutter.
  disableBodyGutter?: boolean
  // Header children that draw their own divider (tabs inset by the header gutter) opt out of the full-width one.
  disableHeaderDivider?: boolean
  type?: 'validate' | 'destroy' | 'primary'

  title: string

  onOpen?: () => void

  onClose: () => void
  onCloseLabel?: string

  onDismiss?: () => void

  // Returning the submit's promise keeps the button locked with a loader until it settles.
  onSubmit: (e: FormEvent<HTMLFormElement>) => void | Promise<unknown>
  onSubmitLabel: string

  onSubmitDisabled?: boolean
  // Shows the loader on the submit button and blocks resubmits, e.g. while the caller's mutation runs.
  loading?: boolean

  showPrevious?: boolean
  onPrevious?: () => void

  showFooter?: boolean
  FooterChildren?: () => JSX.Element

  overflow?: 'auto' | 'hidden'

  // Render callback, not a component type: inline callbacks must not remount the header.
  HeaderChildren?: () => JSX.Element

  style?: React.CSSProperties
}

export const PopUpForm = (props: PopUpSkeletonProps & { dialog?: boolean; alert?: boolean }): JSX.Element => {
  const {
    dialog = false,
    alert = false,
    children,
    title,
    onOpen,
    onClose,
    onCloseLabel,
    onDismiss,
    onSubmit,
    onSubmitLabel,
    showFooter = true,
    onPrevious,
    showPrevious,
    fullScreen,
    disableBodyGutter,
    disableHeaderDivider,
    type = 'validate',
    onSubmitDisabled,
    loading,
    HeaderChildren: renderHeader,
    FooterChildren,
    overflow = 'auto',
    style,
  } = props

  const { t } = useTranslation()
  const popupRef = useRef<HTMLDivElement>(null)
  const cancelRef = useRef<HTMLButtonElement>(null)

  // The ref closes the gap before the re-render: a double click must not run the submit twice.
  const isSubmittingRef = useRef<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const { isHeld, holdSubmit } = useSubmitHolds()
  const isBusy = loading || isSubmitting || isHeld

  useEffect(() => {
    onOpen?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    if (isSubmittingRef.current || isBusy) {
      e.preventDefault()
      e.stopPropagation()
      return
    }

    const result = onSubmit(e)
    if (!(result instanceof Promise)) return

    isSubmittingRef.current = true
    setIsSubmitting(true)
    try {
      await result
    } finally {
      isSubmittingRef.current = false
      setIsSubmitting(false)
    }
  }

  const content = (
    <>
      <HeaderContainer hasChildren={!!renderHeader} disableDivider={disableHeaderDivider}>
        <HeaderTitleContainer>
          <TitleContainer>
            {showPrevious && onPrevious && (
              <PreviousButton type="button" aria-label={t('components_PopUp_label_previous')} onClick={onPrevious}>
                <ArrowLeftIcon />
              </PreviousButton>
            )}
            {dialog ? <Dialog.Title render={<Title />}>{title}</Dialog.Title> : <Title>{title}</Title>}
          </TitleContainer>

          <CloseButton type="button" aria-label={t('components_PopUp_label_dismiss')} onClick={onDismiss ?? onClose}>
            <CloseIcon />
          </CloseButton>
        </HeaderTitleContainer>

        {renderHeader?.()}
      </HeaderContainer>

      <ScrollContainer overflow={overflow}>
        <MainContainer fullScreen={fullScreen} disableBodyGutter={disableBodyGutter}>
          <SubmitHoldProvider value={holdSubmit}>{children}</SubmitHoldProvider>
        </MainContainer>
      </ScrollContainer>

      {showFooter && (
        <BottomContainer>
          {FooterChildren ? (
            <FooterChildren />
          ) : (
            <>
              <CancelButton ref={cancelRef} type="button" onClick={onClose}>
                {onCloseLabel || t('components_PopUp_label_close')}
              </CancelButton>

              {(type == 'validate' || type == 'destroy') && (
                <Button
                  type="submit"
                  variant="solid"
                  color={type == 'destroy' ? 'danger' : 'success'}
                  size={{ default: 'medium' }}
                  disabled={onSubmitDisabled}
                  isLoading={isBusy}
                >
                  {onSubmitLabel}
                </Button>
              )}

              {type == 'primary' && (
                <Button variant="solid" color="primary" size={{ default: 'medium' }} isLoading={isBusy}>
                  {onSubmitLabel}
                </Button>
              )}
            </>
          )}
        </BottomContainer>
      )}
    </>
  )

  return dialog ? (
    <DialogForm
      render={<form onSubmit={handleSubmit} />}
      ref={popupRef}
      data-modal-window=""
      initialFocus={() =>
        (alert ? cancelRef.current : focusFirstField(popupRef.current)) ?? cancelRef.current ?? popupRef.current
      }
      style={style}
    >
      {content}
    </DialogForm>
  ) : (
    <PopUpContainer onSubmit={handleSubmit} style={style}>
      {content}
    </PopUpContainer>
  )
}
